/**
 * Shortest unique campaign ancestor — tab crumbs and the doc-editor
 * "You're in …" line. Cloned templates reuse nested names, so the
 * immediate campaign/mission is often the same on every copy. Walk
 * each campaign's root→leaf path and keep the shortest prefix that
 * no other same-named campaign shares.
 */

export function normalizeCampaignName(name) {
  const trimmed = String(name || '').trim();
  return trimmed || 'Untitled';
}

function sprintMap(sprintsOrMap) {
  if (sprintsOrMap instanceof Map) return sprintsOrMap;
  const map = new Map();
  for (const sprint of sprintsOrMap || []) {
    if (sprint?.id && !sprint.deletedAt) map.set(sprint.id, sprint);
  }
  return map;
}

function nodeFromSprint(sprint) {
  return {
    id: sprint.id,
    name: normalizeCampaignName(sprint.name),
    icon: sprint.icon || null,
  };
}

/** Root → leaf ancestry for a campaign. Stops on a missing/deleted parent. */
export function buildCampaignAncestry(sprintId, sprintsOrMap) {
  if (!sprintId) return [];
  const byId = sprintMap(sprintsOrMap);
  const path = [];
  const seen = new Set();
  let current = byId.get(sprintId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(nodeFromSprint(current));
    current = current.parentSprintId ? byId.get(current.parentSprintId) : null;
  }
  return path;
}

export function formatCampaignPath(path = []) {
  return (path || [])
    .map((node) => normalizeCampaignName(node?.name))
    .filter(Boolean)
    .join(' / ');
}

/** True when `prefixPath` stops before the end of `fullPath` (skipped nests). */
export function pathElidesRest(prefixPath = [], fullPath = []) {
  return (fullPath || []).length > (prefixPath || []).length;
}

/**
 * Join a unique prefix. Append ` /.../` when later campaigns were skipped
 * so `campaign 1 / DOC` does not read as a direct child path.
 */
export function formatElidedCampaignPath(prefixPath = [], fullPath = []) {
  const label = formatCampaignPath(prefixPath);
  if (!label) return '';
  return pathElidesRest(prefixPath, fullPath) ? `${label} /.../` : label;
}

function pathNames(path) {
  return (path || []).map((node) => normalizeCampaignName(node?.name));
}

/**
 * Shortest root→leaf prefix of `path` that no peer path shares.
 * No peers (leaf name is unique) → just the leaf, i.e. the current campaign.
 */
export function shortestUniqueAncestorPath(path = [], peerPaths = []) {
  if (!path.length) return [];
  if (!peerPaths.length) return [path[path.length - 1]];

  const names = pathNames(path);
  const peerNameLists = peerPaths.map(pathNames);

  for (let k = 1; k <= names.length; k += 1) {
    const prefix = names.slice(0, k);
    const collision = peerNameLists.some(
      (peer) =>
        peer.length >= k && prefix.every((name, i) => name === peer[i])
    );
    if (!collision) return path.slice(0, k);
  }

  return path;
}

/** Crumb for a campaign tab: unique prefix minus the campaign itself (that's the title). */
export function campaignTabCrumbPath(uniquePath = [], currentId) {
  if (!uniquePath.length) return [];
  if (uniquePath[uniquePath.length - 1]?.id === currentId) {
    return uniquePath.slice(0, -1);
  }
  return uniquePath;
}

/**
 * Index every live campaign's full path and unique ancestor prefix.
 * Comparison set is workspace-wide (not just open tabs) so labels stay put.
 */
export function indexUniqueCampaignAncestors(sprints = []) {
  const byId = sprintMap(sprints);
  const pathsById = new Map();
  for (const id of byId.keys()) {
    pathsById.set(id, buildCampaignAncestry(id, byId));
  }

  const groups = new Map();
  for (const [id, path] of pathsById) {
    const leaf = normalizeCampaignName(path[path.length - 1]?.name);
    const group = groups.get(leaf);
    if (group) group.push(id);
    else groups.set(leaf, [id]);
  }

  const uniqueById = new Map();
  for (const [id, path] of pathsById) {
    const leaf = normalizeCampaignName(path[path.length - 1]?.name);
    const peers = (groups.get(leaf) || [])
      .filter((peerId) => peerId !== id)
      .map((peerId) => pathsById.get(peerId));
    uniqueById.set(id, shortestUniqueAncestorPath(path, peers));
  }

  return { pathsById, uniqueById };
}

export function uniqueCampaignAncestorPath(sprintId, sprints = []) {
  if (!sprintId) return [];
  const { uniqueById } = indexUniqueCampaignAncestors(sprints);
  return uniqueById.get(sprintId) || buildCampaignAncestry(sprintId, sprints);
}

/** Doc-editor location: unique ancestor + mission. Hover tip is the full path. */
export function missionDocLocation({
  campaignId,
  campaignName,
  missionName,
  sprints,
} = {}) {
  const nameLabel = String(missionName || '').trim() || 'this mission';
  const fallback = campaignName
    ? [{ id: campaignId, name: normalizeCampaignName(campaignName) }]
    : [];
  const ancestry = buildCampaignAncestry(campaignId, sprints);
  const unique = uniqueCampaignAncestorPath(campaignId, sprints);
  const ancestorNodes = unique.length ? unique : fallback;
  const tipNodes = ancestry.length ? ancestry : fallback;
  const ancestorLabel = formatCampaignPath(ancestorNodes);
  if (!ancestorLabel) {
    return { displayName: nameLabel, tip: nameLabel };
  }
  const displayName = pathElidesRest(ancestorNodes, tipNodes)
    ? `${ancestorLabel} /.../ ${nameLabel}`
    : `${ancestorLabel} › ${nameLabel}`;
  return {
    displayName,
    tip: formatCampaignPath([...tipNodes, { name: nameLabel }]) || nameLabel,
  };
}
